import { Capacitor, registerPlugin } from '@capacitor/core';

const AppUpdate = registerPlugin('AppUpdate');
let installedVersionPromise = null;

const isNativeAndroid = () => {
  try { return Capacitor.isNativePlatform?.() && Capacitor.getPlatform?.() === 'android'; } catch { return false; }
};

const parseVersion = (value) => {
  const text = String(value || '').trim().replace(/^v/i, '');
  if (!/^\d+(?:\.\d+){1,3}$/.test(text)) return null;
  return text.split('.').map((part) => Number(part));
};

export const isVersionBelow = (installed, minimum) => {
  const a = parseVersion(installed);
  const b = parseVersion(minimum);
  if (!a || !b) return false;
  const size = Math.max(a.length, b.length);
  for (let index = 0; index < size; index += 1) {
    const left = a[index] || 0;
    const right = b[index] || 0;
    if (left !== right) return left < right;
  }
  return false;
};

export const getInstalledAppVersion = async () => {
  if (!isNativeAndroid()) return null;
  if (!installedVersionPromise) {
    installedVersionPromise = (async () => {
      try {
        const result = await AppUpdate.getAppVersion();
        return typeof result?.version === 'string' ? result.version : null;
      } catch {
        // A hosted web bundle is not evidence of the installed Android build.
        // Fail open on legacy shells rather than falsely blocking a current one.
        return null;
      }
    })();
  }
  return installedVersionPromise;
};

export const requiredUpdateForDriver = async (notice) => {
  if (!isNativeAndroid() || !notice?.enabled || notice?.type !== 'required_update') return null;
  if (!['driver', 'all'].includes(notice.audience)) return null;
  const minimumVersion = String(notice.driverMinVersion || '').trim();
  // Unknown native version means a legacy Anna shell without this bridge. When
  // an MFA admin has deliberately enabled a valid gate, it must update too;
  // the HTTPS Play fallback still works without the native plugin.
  if (!parseVersion(minimumVersion)) return null;
  const installedVersion = await getInstalledAppVersion();
  if (installedVersion && !isVersionBelow(installedVersion, minimumVersion)) return null;
  return {
    title: String(notice.title || '').trim().slice(0, 100) || 'Update required',
    message: String(notice.message || '').trim().slice(0, 500) || 'Update MyTownly Anna to continue receiving ride requests.',
    minimumVersion,
  };
};

export const startRequiredUpdate = async () => {
  try {
    const result = await AppUpdate.startImmediateUpdate();
    if (result?.started) return true;
  } catch (err) {
    console.warn('Immediate Play update unavailable:', err?.message || err);
  }
  try {
    await AppUpdate.openStoreListing();
    return true;
  } catch (err) {
    console.warn('Play Store fallback unavailable:', err?.message || err);
    try {
      window.location.assign('https://play.google.com/store/apps/details?id=in.mytownly.rides.driver');
      return true;
    } catch {
      return false;
    }
  }
};
